import { LiveData, OnEvent, Service } from '@toeverything/infra';

import type { GlobalState } from '../../storage';
import { WorkbenchLocationChanged } from '../../workbench/services/workbench';
import type { WorkspacesService } from '../../workspace';

const storageKey = 'open-link-mode';

export enum OpenLinkMode {
  ALWAYS_ASK = 'always-ask', // default
  OPEN_IN_WEB = 'open-in-web',
  OPEN_IN_DESKTOP_APP = 'open-in-desktop-app',
}

@OnEvent(WorkbenchLocationChanged, e => e.onNavigation)
export class OpenInAppService extends Service {
  private initialized = false;

  private initialUrl: string | undefined;

  readonly showOpenInAppBanner$ = new LiveData<boolean>(false);
  readonly showOpenInAppPage$ = new LiveData<boolean | undefined>(undefined);

  constructor(
    public readonly globalState: GlobalState,
    public readonly workspacesService: WorkspacesService
  ) {
    super();
  }

  onNavigation() {
    // check doc id instead?
    if (window.location.href === this.initialUrl) {
      return;
    }
    this.showOpenInAppBanner$.next(false);
  }

  /**
   * Given the initial URL, check if we need to redirect to the desktop app.
   */
  bootstrap() {
    if (this.initialized || !window) {
      return;
    }

    this.initialized = true;
    this.initialUrl = window.location.href;

    // Card 1864175759736702612 — o "abrir no aplicativo" NAO EXISTE nesta instalacao.
    //
    // O upstream mostrava, ao abrir um link de documento direto (que e o gesto de quem RECEBE
    // um compartilhamento), um cartao dizendo "Open this doc in AFFiNE app" com um link de
    // download. Nao e so marca errada: o aplicativo de desktop da AFFiNE **nao fala com este
    // servidor**, e nao ha aplicativo de Mirezi. Quem clicasse sairia do ambiente sem entender
    // por que. E a mesma decisao que tirou a barra de marketing da tela de login no card
    // 1854540586736944835 — sair, nao traduzir.
    //
    // ⚠️ A neutralizacao mora AQUI, e nao no ponto em que o cartao e montado, por causa de um
    // caso que so aparece em ambiente ja usado: quem clicou "lembrar minha escolha" no cartao
    // tem `open-link-mode: open-in-desktop-app` gravado no proprio navegador. Desmontar so o
    // cartao deixaria essa pessoa sendo REDIRECIONADA para um aplicativo inexistente, para
    // sempre, sem nenhuma forma de desfazer — porque o controle que desfaz era o cartao.
    // Cortando na raiz, o valor velho simplesmente deixa de ser consultado.
    //
    // O que NAO se toca: `getOpenLinkMode`/`setOpenLinkMode` e o `showOpenInAppPage()`
    // explicito seguem existindo. O primeiro porque a `/open-app` do fluxo de login do
    // desktop ainda e codigo do upstream que nao queremos reescrever; o segundo porque quem o
    // chamava era o item de menu que este mesmo card remove.
    this.showOpenInAppBanner$.next(false);
    this.showOpenInAppPage$.next(false);
  }

  showOpenInAppPage() {
    this.showOpenInAppPage$.next(true);
  }

  hideOpenInAppPage() {
    this.showOpenInAppPage$.next(false);
  }

  getOpenLinkMode() {
    return (
      this.globalState.get<OpenLinkMode>(storageKey) ?? OpenLinkMode.ALWAYS_ASK
    );
  }

  openLinkMode$ = LiveData.from(
    this.globalState.watch<OpenLinkMode>(storageKey),
    this.getOpenLinkMode()
  ).map(v => v ?? OpenLinkMode.ALWAYS_ASK);

  setOpenLinkMode(mode: OpenLinkMode) {
    this.globalState.set(storageKey, mode);
  }

  dismissBanner(rememberMode: OpenLinkMode | undefined) {
    if (rememberMode) {
      this.globalState.set(storageKey, rememberMode);
    }
    this.showOpenInAppBanner$.next(false);
  }
}
